import {chain, invert} from 'lodash';

import {makeEnumRuntimeType} from './apiTypes';

/*
 * Runtime type.
 */

export enum FutureTimeValuesEnum {
  ADD_2_MINUTES = 'ADD_2_MINUTES',
  ADD_10_MINUTES = 'ADD_10_MINUTES',
  ADD_20_MINUTES = 'ADD_20_MINUTES',
  ADD_30_MINUTES = 'ADD_30_MINUTES',
  ADD_40_MINUTES = 'ADD_40_MINUTES',
  ADD_50_MINUTES = 'ADD_50_MINUTES',
  ADD_1_HOUR = 'ADD_1_HOUR',
  ADD_1_HOUR_HALF = 'ADD_1_HOUR_HALF',
  ADD_2_HOURS = 'ADD_2_HOURS',
  ADD_3_HOURS = 'ADD_3_HOURS',
  ADD_4_HOURS = 'ADD_4_HOURS',
  ADD_5_HOURS = 'ADD_5_HOURS',
  ADD_6_HOURS = 'ADD_6_HOURS',
  ADD_8_HOURS = 'ADD_8_HOURS',
  ADD_10_HOURS = 'ADD_10_HOURS',
  ADD_12_HOURS = 'ADD_12_HOURS',
  TOMORROW_9AM = 'TOMORROW_9AM',
  TOMORROW_2PM = 'TOMORROW_2PM',
  ADD_2_DAYS = 'ADD_2_DAYS',
  ADD_3_DAYS = 'ADD_3_DAYS',
  ADD_4_DAYS = 'ADD_4_DAYS',
  ADD_5_DAYS = 'ADD_5_DAYS',
  ADD_6_DAYS = 'ADD_6_DAYS',
  ADD_1_WEEK = 'ADD_1_WEEK',
  ADD_10_DAYS = 'ADD_10_DAYS',
  ADD_2_WEEKS = 'ADD_2_WEEKS',
  ADD_3_WEEKS = 'ADD_3_WEEKS',
  ADD_1_MONTH = 'ADD_1_MONTH',
  ADD_2_MONTHS = 'ADD_2_MONTHS',
  ADD_3_MONTHS = 'ADD_3_MONTHS',
}

export const RFutureTimeValue = makeEnumRuntimeType(FutureTimeValuesEnum);

/*
 * Type.
 */

export type FutureTimeValueModel = FutureTimeValuesEnum;

/*
 * IO methods.
 */

const futureTimeProperties = ['m', 'd', 'h'];
const splitFutureTimeValueFragmentRegexp = new RegExp(`([\\d]+)([${futureTimeProperties.join('')}])`); // 1d => [1, d]

// Parse.
export function parseFutureTimeValue(src: any) {
  const reminderKey = findKeyForFutureTime(src);
  return futureTimesMap[reminderKey];
}

function findKeyForFutureTime(reminder: any) {
  return chain(futureTimeProperties)
    .map((property) => reminder[property] && `${reminder[property]}${property}`)
    .compact()
    .join('-')
    .value();
}

// Serialize.
export function serializeFutureTimeValue(reminderValue: FutureTimeValueModel) {
  const reminderKey = futureTimesKeyMap[reminderValue];
  if (!reminderKey) {
    return null;
  }

  return reminderKey.split('-').reduce(
    (result, fragment) => ({
      ...result,
      ...futureTimeValueFragmentToObject(fragment),
    }),
    {},
  );
}

function futureTimeValueFragmentToObject(fragment: string) {
  const match = fragment.match(splitFutureTimeValueFragmentRegexp);
  if (!match) {
    return null;
  }

  const number = Number(match[1]);
  const unit = match[2];

  return {[unit]: number};
}

type FutureTimesMap = {[key: string]: FutureTimeValuesEnum | undefined};
const futureTimesMap: FutureTimesMap = {
  '2m': FutureTimeValuesEnum.ADD_2_MINUTES,
  '10m': FutureTimeValuesEnum.ADD_10_MINUTES,
  '20m': FutureTimeValuesEnum.ADD_20_MINUTES,
  '30m': FutureTimeValuesEnum.ADD_30_MINUTES,
  '40m': FutureTimeValuesEnum.ADD_40_MINUTES,
  '50m': FutureTimeValuesEnum.ADD_50_MINUTES,
  '60m': FutureTimeValuesEnum.ADD_1_HOUR,
  '90m': FutureTimeValuesEnum.ADD_1_HOUR_HALF,
  '120m': FutureTimeValuesEnum.ADD_2_HOURS,
  '180m': FutureTimeValuesEnum.ADD_3_HOURS,
  '240m': FutureTimeValuesEnum.ADD_4_HOURS,
  '300m': FutureTimeValuesEnum.ADD_5_HOURS,
  '360m': FutureTimeValuesEnum.ADD_6_HOURS,
  '480m': FutureTimeValuesEnum.ADD_8_HOURS,
  '600m': FutureTimeValuesEnum.ADD_10_HOURS,
  '720m': FutureTimeValuesEnum.ADD_12_HOURS,
  '1d-9h': FutureTimeValuesEnum.TOMORROW_9AM,
  '1d-14h': FutureTimeValuesEnum.TOMORROW_2PM,
  '2d': FutureTimeValuesEnum.ADD_2_DAYS,
  '3d': FutureTimeValuesEnum.ADD_3_DAYS,
  '4d': FutureTimeValuesEnum.ADD_4_DAYS,
  '5d': FutureTimeValuesEnum.ADD_5_DAYS,
  '6d': FutureTimeValuesEnum.ADD_6_DAYS,
  '7d': FutureTimeValuesEnum.ADD_1_WEEK,
  '10d': FutureTimeValuesEnum.ADD_10_DAYS,
  '14d': FutureTimeValuesEnum.ADD_2_WEEKS,
  '21d': FutureTimeValuesEnum.ADD_3_WEEKS,
  '30d': FutureTimeValuesEnum.ADD_1_MONTH,
  '60d': FutureTimeValuesEnum.ADD_2_MONTHS,
  '90d': FutureTimeValuesEnum.ADD_3_MONTHS,
};

export const futureTimesKeyMap = invert(futureTimesMap);
